import { IHAChangeDistrict } from '../interfaces/IHAChangeDistrict';
import { IHAChangePollingPlace } from '../interfaces/IHAChangePollingPlace';
import { IHAStaticDistrict } from '../interfaces/IHAStaticDistrict';
import { CandidateCount } from './CandidateCount';
import { PollingPlaceCount } from './PollingPlaceCount';


export class DistrictCount {

  private readonly _haStaticDistrict: IHAStaticDistrict;
  private readonly _haChangeDistrict: IHAChangeDistrict;
  private readonly _electionDate: Date;

  private totalAllCandidatesCount = 0;
  private totalDistrictInformalVotes = 0;
  private totalDistrictPollingBooths = 0;
  private totalDistrictPollingBoothsWithCounts = 0;
  private totalDistrictEarlyVotingCentres = 0;
  private totalDistrictEarlyVotingCentresWithCounts = 0;
  private totalDistrictDeclarationTypes = 0;
  private totalDistrictDeclarationTypesWithCounts = 0;
  private districtTotalFormalVotesValue = 0;
  private previousTotalFirstPrefVotesValue = 0;
  private districtFormalDeclarationVotesValue = 0;
  private twoCPCandidate1Value: CandidateCount | null = null;
  private twoCPCandidate2Value: CandidateCount | null = null;

  public candidates: CandidateCount[] = [];
  public pollingPlaces: PollingPlaceCount[] = [];
  private leadingCandidateValue: CandidateCount | null = null;
  private previousPartyNameValue = '';

  // Helper function to check if polling place is a polling booth
  private isPollingBooth(type: string): boolean {
    return (
      type === 'PB' ||
      type === 'Polling Booth' ||
      type === 'S1' ||
      type === 'Shared Booth (Host)' ||
      type === 'S2' ||
      type === 'Shared Booth (Visitor 1)' ||
      type === 'S3' ||
      type === 'Shared Booth (Visitor 2)' ||
      type === 'MT' ||
      type === 'Mobile Poll Team'
    );
  }

  // Helper function to check if polling place is an early voting centre
  private isEarlyVotingCentre(type: string): boolean {
    return (
      type === 'PP' ||
      type === 'P1' ||
      type === 'P2' ||
      type === 'P3' ||
      type === 'Early Voting Centre' ||
      type === 'Shared Early Voting Centre (Host)' ||
      type === 'Shared Early Voting Centre (Visitor 1)' ||
      type === 'Shared Early Voting Centre (Visitor 2)'
    );
  }

  constructor(
    electionDate: Date,
    haStaticDistrict: IHAStaticDistrict,
    haChangeDistrict: IHAChangeDistrict
  ) {
    this._haStaticDistrict = haStaticDistrict;
    this._haChangeDistrict = haChangeDistrict;
    this._electionDate = electionDate;
 

    const tmpTotalDistrictVotes = haChangeDistrict.candidates
      .reduce((sum, current) => sum + current.declarationVotes + current.ordinaryVotes, 0);
   

    let tmpTotalTwoCandidatePref = 0;
    // haChangeDistrict.pollingPlaces.sort((a, b) => a.pollingPlaceName < b.pollingPlaceName ? -1 : 1);
    haChangeDistrict.pollingPlaces.forEach(pollingPlace => {
      pollingPlace.pollingCandidates.filter(x => x.twoCandidatePref !== null).forEach(pollingCandidate => {
        tmpTotalTwoCandidatePref += pollingCandidate.twoCandidatePref ?? 0;
      });
    });
    haChangeDistrict.candidates.forEach(candidate => {
      tmpTotalTwoCandidatePref += candidate.twoCandidatePrefDeclarationVotes ?? 0;
    });

    this._haStaticDistrict.candidates.forEach(candidate => {
      const haStaticCadidate = haStaticDistrict.candidates.find(x => x.candidateId === candidate.candidateId);
      const haChangeCandidate = haChangeDistrict.candidates.find(x => x.candidateId === candidate.candidateId);
      const CandidatePollingPlaces: IHAChangePollingPlace[] = this._haChangeDistrict.pollingPlaces.map(item => {
        return {
          pollingPlaceName: item.pollingPlaceName,
          informalVotes: +item.informalVotes,
          pollingCandidates: item.pollingCandidates.filter(x => x.candidateId === candidate.candidateId)
        };
      });

      if (haStaticCadidate && haChangeCandidate) {
        this.candidates.push(new CandidateCount(
          haStaticCadidate,
          haChangeCandidate,
          CandidatePollingPlaces,
          tmpTotalDistrictVotes,
          tmpTotalTwoCandidatePref
        ));
      }
    });
    this._haStaticDistrict.pollingPlaces.forEach(pollingPlace => {
      const haStaticPollingPlace = haStaticDistrict.pollingPlaces.find(x => x.pollingPlaceName === pollingPlace.pollingPlaceName);
      const haChangePollingPlace = haChangeDistrict.pollingPlaces.find(x => x.pollingPlaceName === pollingPlace.pollingPlaceName);

      if (haStaticPollingPlace && haChangePollingPlace) {
        this.pollingPlaces.push(new PollingPlaceCount(
          haStaticPollingPlace,
          this._haStaticDistrict.candidates,
          haChangePollingPlace
        ));
      }
    });
  }

  public afterInit(): void {
    this.pollingPlaces.forEach(pollingPlace => pollingPlace.afterInit());
    this.candidates.forEach(candidate => candidate.afterInit());

    this.previousTotalFirstPrefVotesValue = this.candidates
      .reduce((sum, current) => sum + current.previousFirstPrefVotes, 0);
    // calc totals here
    // Use a sorted *copy* so we don't change the original ballot-paper order
    const sortedCandidates = [...this.candidates]
      .sort((a, b) => b.totalPollingPlaceTwoCandidatePrefVotes - a.totalPollingPlaceTwoCandidatePrefVotes);

    this.leadingCandidateValue = sortedCandidates.length > 0 && sortedCandidates[0].totalPollingPlaceTwoCandidatePrefVotes !== 0 ?
      sortedCandidates[0] : null;

    this.totalAllCandidatesCount = this.candidates
      .reduce((sum, current) => sum + current.declarationVoteCount + current.ordinaryVoteCount, 0);

    this.districtFormalDeclarationVotesValue = this.candidates.reduce((sum, current) => sum + current.declarationVoteCount, 0);

    this.totalDistrictInformalVotes = this.pollingPlaces.reduce((sum, current) => sum + current.informalVotes, 0) +
      this._haChangeDistrict.informalDeclarationVotes;

    this.districtTotalFormalVotesValue = this.candidates
      .reduce((sum, current) => sum + current.ordinaryVoteCount + current.declarationVoteCount, 0);


    this.totalDistrictPollingBooths = this._haStaticDistrict.pollingPlaces
      .filter(x => this.isPollingBooth(x.pollingPlaceType)).length;

    this.totalDistrictPollingBoothsWithCounts = this.pollingPlaces
      .filter(x => this.isPollingBooth(x.pollingPlaceType) && x.totalFormalVotes > 0).length;

    this.totalDistrictEarlyVotingCentres = this._haStaticDistrict.pollingPlaces
      .filter(x => this.isEarlyVotingCentre(x.pollingPlaceType)).length;

    this.totalDistrictEarlyVotingCentresWithCounts = this.pollingPlaces
      .filter(x => this.isEarlyVotingCentre(x.pollingPlaceType) && x.totalFormalVotes > 0).length;

    this.totalDistrictDeclarationTypes = this._haChangeDistrict.declarations?.length ?? 0;

    this.totalDistrictDeclarationTypesWithCounts = this._haChangeDistrict.declarations
      ?.filter(d => d.formalVotes > 0).length ?? 0;

    this.twoCPCandidate1Value = null;
    this.twoCPCandidate2Value = null;
    const tmpTwoCPCandidates = this.candidates.filter(x => x.totalPollingPlaceTwoCandidatePrefVotes > 0);
    if (tmpTwoCPCandidates.length === 2) {
      // Order by position in district candidates list (same as First preferences by voting centre & vote type)
      const index0 = this.candidates.indexOf(tmpTwoCPCandidates[0]);
      const index1 = this.candidates.indexOf(tmpTwoCPCandidates[1]);
      if (index0 <= index1) {
        this.twoCPCandidate1Value = tmpTwoCPCandidates[0];
        this.twoCPCandidate2Value = tmpTwoCPCandidates[1];
      } else {
        this.twoCPCandidate1Value = tmpTwoCPCandidates[1];
        this.twoCPCandidate2Value = tmpTwoCPCandidates[0];
      }
    }
  }


  public get twoCPCandidate1(): CandidateCount | null {
    return this.twoCPCandidate1Value;
  }

  public get twoCPCandidate2(): CandidateCount | null {
    return this.twoCPCandidate2Value;
  }

  public get districtInformalVotes(): number {
    return this.totalDistrictInformalVotes;
  }

  public get leadingCandidate(): CandidateCount | null {
    return this.leadingCandidateValue;
  }

  public get districtCandidatesVotes(): number {
    return this.totalAllCandidatesCount;
  }

  public get distributionCountDate(): Date | null {
    return this._haChangeDistrict.distributionCountDate;
  }

  public get districtName(): string {
    return this._haStaticDistrict.districtName;
  }

  public get districtFormalDeclarationVotes(): number {
    return this.districtFormalDeclarationVotesValue;
  }

  public get districtInformalDeclarationVotes(): number {
    return this._haChangeDistrict.informalDeclarationVotes;
  }

  public get districtEnrolled(): number {
    return this._haStaticDistrict.districtEnrolled;
  }

  public get districtPollingBooths(): number {
    return this.totalDistrictPollingBooths;
  }

  public get districtPollingBoothsWithCounts(): number {
    return this.totalDistrictPollingBoothsWithCounts;
  }

  public get pollingBoothCountPercent(): number {
    return this.totalDistrictPollingBooths > 0 ?
      (this.totalDistrictPollingBoothsWithCounts / this.totalDistrictPollingBooths) * 100 : 0;
  }

  public get previousPartyName(): string {
    return this.previousPartyNameValue;
  }

  public get profileId(): string {
    return this._haStaticDistrict.profileId;
  }

  public get mapPath(): string {
    return `assets/maps/${this._electionDate.getFullYear().toString()}/${this.districtName}.jpg`;
  }

  public get districtTotalVotesCast(): number {
    return this.districtTotalFormalVotesValue + this.districtInformalVotes;
  }

  public get districtTotalFormalVotes(): number {
    return this.districtTotalFormalVotesValue;
  }

  public get districtFormalVotesPercent(): number {
    return this.districtTotalVotesCast > 0 ?
      (this.districtTotalFormalVotesValue / this.districtTotalVotesCast) * 100 : 0;
  }

  public get districtInformalVotesPercent(): number {
    return this.districtTotalVotesCast > 0 ?
    (this.totalDistrictInformalVotes / this.districtTotalVotesCast) * 100 : 0;
  }

  public get districtTurnoutPercent(): number {
    return this.districtEnrolled > 0 && this.districtTotalVotesCast > 0 ?
      (this.districtTotalVotesCast / this.districtEnrolled) * 100 : 0;
  }

  public get previousTotalFirstPrefVotes(): number {
    return this.previousTotalFirstPrefVotesValue;
  }

  public get districtEarlyVotingCentres(): number {
    return this.totalDistrictEarlyVotingCentres;
  }

  public get districtEarlyVotingCentresWithCounts(): number {
    return this.totalDistrictEarlyVotingCentresWithCounts;
  }

  public get earlyVotingCentreCountPercent(): number {
    return this.totalDistrictEarlyVotingCentres > 0
      ? (this.totalDistrictEarlyVotingCentresWithCounts / this.totalDistrictEarlyVotingCentres) * 100
      : 0;
  }

  public get districtDeclarationTypes(): number {
    return this.totalDistrictDeclarationTypes;
  }

  public get districtDeclarationTypesWithCounts(): number {
    return this.totalDistrictDeclarationTypesWithCounts;
  }

  public get declarationTypeCountPercent(): number {
    return this.totalDistrictDeclarationTypes > 0
      ? (this.totalDistrictDeclarationTypesWithCounts / this.totalDistrictDeclarationTypes) * 100
      : 0;
  }

  public get finalDistribution() {
    return this._haChangeDistrict.finalDistribution;
  }

  public get declarations() {
    return this._haChangeDistrict.declarations;
  }

  public get absentOrdinary() {
    return this._haChangeDistrict.absentOrdinary;
  }

}
